import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, CalendarBlank } from "@phosphor-icons/react";
import api from "@/lib/api";
import SeatsLeft from "@/components/SeatsLeft";

export const PROMO = {
  key: "sep_thanksgiving_2026_dismissed",
  endsAt: "2026-11-15T05:00:00Z",
  coursePath: "/training/thanksgiving-multi-state-ccw-2026",
  image: "/img/promo/thanksgiving-2026.webp",
  alt: "Thanksgiving Special — Multi-State CCW Training, MD · DC · VA · UT, $200 tuition, Nov 13–14 2026",
  accent: "#FFD300",
  eyebrow: "// LIMITED SEATS · THANKSGIVING SPECIAL",
  headline: "Multi-State CCW.",
  highlight: "Four states for $200.",
  body: "Maryland · Washington DC · Virginia · Utah — one training weekend, four state-specific certificates. Normally $350.",
  dates: "Fri, Nov 13 & Sat, Nov 14, 2026",
  datesNote: "SEP HQ · 4061 Powder Mill Rd, Suite 500-8, Calverton, MD 20705",
};

const COOLDOWN_MS = 24 * 60 * 60 * 1000;

export default function PromoPopup() {
  const [open, setOpen] = useState(false);
  const [next, setNext] = useState(null);

  useEffect(() => {
    if (Date.now() > Date.parse(PROMO.endsAt)) return;
    const until = Number(localStorage.getItem(PROMO.key) || 0);
    if (until > Date.now()) return;
    const slug = PROMO.coursePath.split("/").pop();
    api.get(`/courses/${slug}/sessions`).then(({ data }) => {
      const s = (data || []).find((x) => new Date(x.starts_at) > new Date()) || data?.[0];
      if (s) setNext({ capacity: s.capacity, seats_left: Math.max(0, s.capacity - s.enrolled_count) });
    }).catch(() => {});
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    localStorage.setItem(PROMO.key, String(Date.now() + COOLDOWN_MS));
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={close}
          data-testid="promo-popup-overlay"
          role="dialog" aria-modal="true" aria-label={PROMO.alt}
        >
          <motion.div
            className="relative w-full max-w-4xl bg-[#0A0A0A] border grid grid-cols-1 md:grid-cols-12 overflow-hidden max-h-[92vh]"
            style={{ borderColor: `${PROMO.accent}99`, boxShadow: `0 0 80px ${PROMO.accent}40` }}
            initial={{ y: 40, scale: 0.96, opacity: 0 }} animate={{ y: 0, scale: 1, opacity: 1 }} exit={{ y: 20, scale: 0.98, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}
            onClick={(e) => e.stopPropagation()}
            data-testid="promo-popup"
          >
            <button onClick={close} className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center bg-black/70 border border-white/20 text-white hover:bg-[#FFD300] hover:text-black transition-colors" aria-label="Close" data-testid="promo-popup-close">
              <X size={16} weight="bold" />
            </button>

            <Link to={PROMO.coursePath} onClick={close} className="md:col-span-5 block bg-black" data-testid="promo-popup-image-link">
              <img src={PROMO.image} alt={PROMO.alt} className="w-full h-full max-h-[40vh] md:max-h-[92vh] object-cover object-top md:object-contain" />
            </Link>

            <div className="md:col-span-7 p-7 md:p-10 flex flex-col justify-center overflow-y-auto">
              <div className="font-mono text-[11px] tracking-[0.25em] uppercase mb-3" style={{ color: PROMO.accent }}>{PROMO.eyebrow}</div>
              <h2 className="font-heading text-3xl md:text-5xl tracking-tighter leading-[0.95] mb-4">
                {PROMO.headline}<br/><span style={{ color: PROMO.accent }}>{PROMO.highlight}</span>
              </h2>
              <p className="text-neutral-300 text-sm md:text-base leading-relaxed mb-5">
                {PROMO.body} Reserve with a <strong className="text-white">50% deposit ($100)</strong>; balance due before class.
              </p>
              <div className="flex items-start gap-3 border border-white/10 bg-black/40 p-4 mb-6">
                <CalendarBlank size={22} weight="duotone" className="flex-shrink-0 mt-0.5" style={{ color: PROMO.accent }} />
                <div>
                  <div className="font-heading text-lg tracking-tight" data-testid="promo-popup-dates">{PROMO.dates}</div>
                  <div className="text-neutral-400 text-xs mt-1">{PROMO.datesNote}</div>
                  <SeatsLeft next={next} className="mt-2 block" />
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to={PROMO.coursePath} onClick={close} className="inline-flex items-center gap-2 text-black font-mono uppercase tracking-wider text-sm py-4 px-7 hover:brightness-110 transition" style={{ backgroundColor: PROMO.accent }} data-testid="promo-popup-cta">
                  Reserve your seat <ArrowRight size={14} />
                </Link>
                <button onClick={close} className="btn-ghost" data-testid="promo-popup-dismiss">Maybe later</button>
              </div>
              <div className="text-[10px] text-neutral-500 mt-6 leading-relaxed">
                Training certificates support permit applications. State fees and approvals are separate.
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
